import { CERTIFICATE_PREVIEW_LABELS } from '../../constants/labels';

const LOGO_SRC = '/pozytronLogo.svg';

/** Repeated logo header — present at the top of every certificate page. */
export const CertificateLogo = () => (
  <div className="flex justify-center">
    <img
      src={LOGO_SRC}
      alt="Pozytron"
      className="w-[290px] h-[68px] shrink-0"
    />
  </div>
);

/** Technician + client signature lines and the footer — last page only. */
export const CertificateSignatures = ({
  includeTechnicianDetail,
  footerText,
}: {
  includeTechnicianDetail: boolean;
  footerText: string;
}) => (
  <div className="flex flex-col items-center w-full gap-8.25 relative">
    {/* Green success badge — decorative, sits in the gap above signatures */}
    <img
      src="/certSuccessIcon.svg"
      alt=""
      aria-hidden="true"
      className="absolute pointer-events-none select-none"
      style={{ width: 52, height: 52, right: 18, top: -140 }}
    />
    <div className="flex items-start justify-between w-full">
      <div
        className={`flex flex-col gap-2 items-center ${includeTechnicianDetail ? 'w-[256px] px-8' : 'w-59 px-5.5'}`}
      >
        <div className="border-t-[1.6px] border-[#DCDDE0] w-full" />
        <div className="flex flex-col gap-1 items-center">
          <p className="font-roboto text-[14px] leading-5 tracking-[0.25px] text-center text-[#474A4C] w-full wrap-break-word">
            {CERTIFICATE_PREVIEW_LABELS.TECHNICIAN_SIGNATURE}
          </p>
          {includeTechnicianDetail && (
            <p className="font-roboto font-medium text-[14px] leading-5 tracking-[0.25px] text-center text-[#181A1B] whitespace-nowrap">
              {CERTIFICATE_PREVIEW_LABELS.TECHNICIAN_DETAILS_PLACEHOLDER}
            </p>
          )}
        </div>
      </div>
      <div className="w-[256px] px-8 flex flex-col gap-2 items-center ml-auto">
        <div className="border-t-[1.6px] border-[#DCDDE0] w-full" />
        <p className="font-roboto text-[14px] leading-5 tracking-[0.25px] text-center text-[#474A4C] w-full wrap-break-word">
          {CERTIFICATE_PREVIEW_LABELS.CLIENT_SIGNATURE}
        </p>
      </div>
    </div>

    <p className="font-roboto font-medium text-[12px] leading-4 tracking-[0.4px] text-center text-[#2A4455] w-full">
      {footerText}
    </p>
  </div>
);

interface CertificateLayoutProps {
  includeTechnicianDetail: boolean;
  footerText: string;
  children: React.ReactNode;
  /**
   * When true (default) the signature lines + footer are rendered, pinned to
   * the bottom of the available space. On a multi-page certificate this is set
   * only on the last page.
   */
  showSignatures?: boolean;
}

/**
 * The bordered certificate frame: repeated logo header at the top, the page
 * content below it, and — on the last page — the signature/footer block pinned
 * to the bottom. Designed to fill a fixed-height {@link CertificatePage}; falls
 * back to natural height when rendered without a height-constrained parent.
 */
export const CertificateLayout = ({
  includeTechnicianDetail,
  footerText,
  children,
  showSignatures = true,
}: CertificateLayoutProps) => (
  <div className="w-full h-full rounded-[10px] border-[1.6px] border-[rgba(42,68,85,0.3)] px-8 py-6 flex flex-col">
    <div className="mb-8">
      <CertificateLogo />
    </div>

    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex flex-col gap-10">{children}</div>
      {showSignatures && (
        <div className="mt-auto pt-8">
          <CertificateSignatures
            includeTechnicianDetail={includeTechnicianDetail}
            footerText={footerText}
          />
        </div>
      )}
    </div>
  </div>
);
